"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stepOpenApiDocs = void 0;
// Step payload schemas
const StepCreatePayload = {
    type: 'object',
    description: 'Step creation request parameters',
    properties: {
        title: {
            type: 'string',
            description: 'Step title - clear and descriptive name for the onboarding step',
            example: 'Set Up Your Profile',
            required: true
        },
        description: {
            type: 'string',
            description: 'Detailed description of what this step involves and its purpose',
            example: 'Complete your profile information including personal details and preferences',
            required: false
        },
        order: {
            type: 'integer',
            description: 'Order position of this step in the onboarding sequence',
            example: 1,
            minimum: 0,
            required: false
        },
        questions: {
            type: 'array',
            description: 'Array of question objects associated with this step. Each question can be of various types: text, textarea, select, radio, checkbox, number, email, url, or date',
            items: {
                type: 'object',
                properties: {
                    text: {
                        type: 'string',
                        description: 'Question text'
                    },
                    type: {
                        type: 'string',
                        enum: ['text', 'textarea', 'select', 'radio', 'checkbox', 'number', 'email', 'url', 'date'],
                        description: 'Question type - determines how the question is displayed and answered'
                    },
                    required: {
                        type: 'boolean',
                        description: 'Whether this question is required'
                    },
                    options: {
                        type: 'array',
                        description: 'Available options for select/radio/checkbox questions',
                        items: {
                            type: 'object',
                            properties: {
                                value: { type: 'string' },
                                label: { type: 'string' }
                            }
                        }
                    }
                }
            },
            required: false
        },
        active: {
            type: 'boolean',
            description: 'Whether the step is currently active and available',
            default: true,
            required: false
        }
    },
    required: ['title']
};
const StepUpdatePayload = {
    type: 'object',
    description: 'Step update request parameters',
    properties: {
        title: {
            type: 'string',
            description: 'Updated step title',
            example: 'Complete Your Profile Setup',
            required: false
        },
        description: {
            type: 'string',
            description: 'Updated step description',
            example: 'Finalize your profile with all necessary information for personalized experience',
            required: false
        },
        order: {
            type: 'integer',
            description: 'Updated order position',
            minimum: 0,
            required: false
        },
        questions: {
            type: 'array',
            description: 'Updated questions array',
            items: {
                type: 'object',
                properties: {
                    text: { type: 'string' },
                    type: { type: 'string' },
                    required: { type: 'boolean' },
                    options: {
                        type: 'array',
                        items: { type: 'string' }
                    }
                }
            },
            required: false
        },
        active: {
            type: 'boolean',
            description: 'Updated step status',
            required: false
        }
    }
};
const StepResponse = {
    type: 'object',
    properties: {
        id: {
            type: 'number',
            description: 'Unique step identifier'
        },
        title: {
            type: 'string',
            description: 'Step title'
        },
        description: {
            type: 'string',
            description: 'Step description'
        },
        order: {
            type: 'integer',
            description: 'Step order in the sequence'
        },
        isShared: {
            type: 'boolean',
            description: 'Whether the step is shared (super admin only)'
        },
        profession_id: {
            type: 'string',
            description: 'Associated profession ID'
        },
        profession: {
            type: 'object',
            description: 'Associated profession details',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                description: { type: 'string' }
            }
        },
        questions: {
            type: 'array',
            description: 'Questions associated with this step',
            items: {
                type: 'object',
                properties: {
                    id: { type: 'number' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    type: {
                        type: 'string',
                        enum: ['text', 'textarea', 'number', 'select', 'radio', 'checkbox']
                    },
                    options: {
                        type: 'array',
                        description: 'Available options for select/radio/checkbox questions',
                        items: {
                            anyOf: [
                                { type: 'string' },
                                {
                                    type: 'object',
                                    properties: {
                                        value: { type: 'string' },
                                        label: { type: 'string' }
                                    }
                                }
                            ]
                        }
                    },
                    required: { type: 'boolean' },
                    order: { type: 'number' }
                }
            }
        },
        created_at: {
            type: 'string',
            format: 'date-time',
            description: 'Creation timestamp'
        },
        updated_at: {
            type: 'string',
            format: 'date-time',
            description: 'Last update timestamp'
        }
    }
};
const StepListResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean'
        },
        data: {
            type: 'array',
            items: StepResponse
        },
        count: {
            type: 'integer',
            description: 'Total number of steps'
        }
    }
};
exports.stepOpenApiDocs = {
    'step.list': {
        description: 'List all onboarding steps with their questions and configuration',
        responses: {
            200: {
                description: 'Steps retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: {
                            type: 'array',
                            items: StepResponse
                        },
                        count: {
                            type: 'integer',
                            description: 'Total number of steps'
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error - failed to retrieve steps',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5103
                    }
                }
            }
        }
    },
    'step.create': {
        description: 'Create a new onboarding step with questions and configuration',
        payload: {
            body: StepCreatePayload
        },
        responses: {
            200: {
                description: 'Step created successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: StepResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or missing required fields',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            500: {
                description: 'Internal server error - step creation failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5103
                    }
                }
            }
        }
    },
    'step.get': {
        description: 'Get step by ID with associated questions',
        responses: {
            200: {
                description: 'Step retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: {
                            type: 'boolean',
                            example: true
                        },
                        data: StepResponse
                    }
                }
            },
            404: {
                description: 'Step not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5101
                    }
                }
            },
            500: {
                description: 'Internal server error',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5103
                    }
                }
            }
        }
    },
    'step.update': {
        description: 'Update an existing step\'s configuration and questions',
        payload: {
            body: StepUpdatePayload
        },
        responses: {
            200: {
                description: 'Step updated successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    data: {
                        type: 'object',
                        properties: StepResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or invalid parameters',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 9005
                    }
                }
            },
            404: {
                description: 'Step not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5101
                    }
                }
            },
            500: {
                description: 'Internal server error - step update failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5104
                    }
                }
            }
        }
    },
    'step.delete': {
        description: 'Delete step by ID and all associated questions',
        responses: {
            200: {
                description: 'Step deleted successfully',
                returnParams: {
                    success: {
                        type: 'boolean'
                    }
                }
            },
            404: {
                description: 'Step not found',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5101
                    }
                }
            },
            500: {
                description: 'Internal server error - step deletion failed',
                returnParams: {
                    success: {
                        type: 'boolean'
                    },
                    error: {
                        type: 'string'
                    },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling',
                        example: 5105
                    }
                }
            }
        }
    }
};
